(() => {
  const PricingV1 = {
    version: 'V1.6.3',

    interpolateByLog(quantity, startQuantity, endQuantity, startValue, endValue) {
      if (quantity <= startQuantity) return startValue;
      if (quantity >= endQuantity) return endValue;
      const progress = (Math.log(quantity) - Math.log(startQuantity))
        / (Math.log(endQuantity) - Math.log(startQuantity));
      return startValue + ((endValue - startValue) * progress);
    },

    interpolateAnchors(quantity, anchors) {
      if (quantity <= anchors[0].quantity) return anchors[0].value;
      for (let index = 0; index < anchors.length - 1; index += 1) {
        const start = anchors[index];
        const end = anchors[index + 1];
        if (quantity <= end.quantity) {
          return this.interpolateByLog(quantity, start.quantity, end.quantity, start.value, end.value);
        }
      }
      return anchors[anchors.length - 1].value;
    },

    getTenQuantityMargin(partArea) {
      if (partArea <= 100) return 0.65;
      if (partArea >= 225) return 0.55;
      return 0.65 - (((partArea - 100) / 125) * 0.1);
    },

    getTargetMargin(quantity, partArea) {
      return this.interpolateAnchors(quantity, [
        { quantity: 1, value: 0.8 },
        { quantity: 10, value: this.getTenQuantityMargin(partArea) },
        { quantity: 100, value: 0.425 },
        { quantity: 500, value: 0.375 },
        { quantity: 1000, value: 0.35 }
      ]);
    },

    getSmallAreaFloor(quantity, partArea) {
      if (partArea <= 0) return 0;
      const priceTables = {
        25: [990, 350, 160, 125, 120],
        50: [1120, 410, 215, 185, 175],
        100: [1326, 500, 289, 265, 255]
      };
      const quantities = [1, 10, 100, 500, 1000];
      const priceAtAreaAnchor = (areaAnchor) => this.interpolateAnchors(
        quantity,
        quantities.map((anchorQuantity, index) => ({
          quantity: anchorQuantity,
          value: priceTables[areaAnchor][index]
        }))
      );
      const price25 = priceAtAreaAnchor(25);
      if (partArea <= 25) return price25;
      const price50 = priceAtAreaAnchor(50);
      if (partArea <= 50) {
        return price25 + ((price50 - price25) * ((partArea - 25) / 25));
      }
      const price100 = priceAtAreaAnchor(100);
      if (partArea >= 100) return price100;
      return price50 + ((price100 - price50) * ((partArea - 50) / 50));
    },

    calculatePartBase(quantity, partArea, rules) {
      const variableUnitCost = partArea * rules.variableCostPerCm2;
      const fixedOrderLabor = rules.fixedLaborPerPart;
      const directUnitCost = variableUnitCost + (fixedOrderLabor / quantity);
      const directOrderCost = (variableUnitCost * quantity) + fixedOrderLabor;
      const targetMargin = this.getTargetMargin(quantity, partArea);
      const costQuoteUnit = directUnitCost / (1 - targetMargin);
      const preliminaryTotal = costQuoteUnit * quantity;
      const eightyMarginOrderPrice = directOrderCost / 0.2;
      const lowAmountProtectionTotal = rules.lowOrderThreshold > 0
        ? Math.min(eightyMarginOrderPrice, rules.lowOrderThreshold)
        : 0;
      const lowAmountProtectionUnit = lowAmountProtectionTotal / quantity;
      const smallAreaFloorUnit = this.getSmallAreaFloor(quantity, partArea);
      const smallAreaProtectedUnit = smallAreaFloorUnit;
      const baseUnitRaw = Math.max(costQuoteUnit, lowAmountProtectionUnit, smallAreaProtectedUnit);
      return {
        partArea,
        variableUnitCost,
        fixedOrderLabor,
        directUnitCost,
        directOrderCost,
        targetMargin,
        costQuoteUnit,
        preliminaryTotal,
        eightyMarginOrderPrice,
        lowAmountProtectionTotal,
        lowAmountProtectionUnit,
        smallAreaFloorUnit,
        smallAreaProtectedUnit,
        baseUnitRaw
      };
    },

    calculateOrderDiscount(subtotalValue, suppliedRules = {}) {
      const configuredNumber = (value, fallback) => {
        if (value === null || value === undefined || value === '') return fallback;
        const number = Number(value);
        return Number.isFinite(number) ? number : fallback;
      };
      const subtotal = Math.max(0, Math.round(Number(subtotalValue) || 0));
      const enabled = suppliedRules.orderDiscountEnabled === true;
      const startSubtotal = Math.max(0, configuredNumber(suppliedRules.orderDiscountStart, 1));
      const midSubtotal = Math.max(
        startSubtotal + 1,
        configuredNumber(suppliedRules.orderDiscountRateCapSubtotal, startSubtotal + 1)
      );
      const midRate = Math.min(
        0.99,
        Math.max(0, configuredNumber(suppliedRules.orderDiscountMidRate, 0))
      );
      const maxSubtotal = Math.max(
        midSubtotal + 1,
        configuredNumber(suppliedRules.orderDiscountMaxSubtotal, midSubtotal + 1)
      );
      const maxRate = Math.min(
        0.99,
        Math.max(midRate, configuredNumber(suppliedRules.orderDiscountMaxRate, 0))
      );
      const terminalAmount = Math.max(0, Math.round(maxSubtotal * maxRate));
      const configuredMaxAmount = Math.max(
        0,
        Math.round(configuredNumber(suppliedRules.orderDiscountMaxAmount, terminalAmount))
      );
      const maxAmount = Math.min(configuredMaxAmount, terminalAmount);
      const emptyResult = {
        enabled,
        applied: false,
        subtotal,
        startSubtotal,
        rateCapSubtotal: midSubtotal,
        midRate,
        maxSubtotal,
        maxRate,
        maxAmount,
        nominalRate: 0,
        effectiveRate: 0,
        uncappedAmount: 0,
        amount: 0,
        capped: false,
        total: subtotal
      };

      if (!enabled || subtotal <= startSubtotal || maxRate <= 0 || maxAmount <= 0) {
        return emptyResult;
      }

      const nominalRate = this.interpolateAnchors(subtotal, [
        { quantity: startSubtotal, value: 0 },
        { quantity: midSubtotal, value: midRate },
        { quantity: maxSubtotal, value: maxRate }
      ]);
      const uncappedRaw = subtotal * nominalRate;
      const uncappedAmount = Math.max(0, Math.round(uncappedRaw));
      const amount = Math.min(uncappedAmount, maxAmount);

      return {
        ...emptyResult,
        applied: amount > 0,
        nominalRate,
        effectiveRate: subtotal > 0 ? amount / subtotal : 0,
        uncappedAmount,
        amount,
        capped: uncappedAmount >= maxAmount,
        total: subtotal - amount
      };
    },

    calculateCombinationDiscount(unitPriceValue, quantityValue, partUnitPricesValue, suppliedRules = {}) {
      const configuredNumber = (value, fallback) => {
        if (value === null || value === undefined || value === '') return fallback;
        const number = Number(value);
        return Number.isFinite(number) ? number : fallback;
      };
      const unitPrice = Math.max(0, Math.round(Number(unitPriceValue) || 0));
      const quantity = Math.max(1, Math.floor(Number(quantityValue) || 1));
      const partUnitPrices = Array.isArray(partUnitPricesValue)
        ? partUnitPricesValue
          .map((price) => Math.max(0, Number(price) || 0))
          .filter((price) => price > 0)
        : [];
      const partCount = partUnitPrices.length;
      const enabled = suppliedRules.combinationDiscountEnabled === true;
      const threshold = Math.max(
        0,
        Math.round(configuredNumber(suppliedRules.combinationDiscountThreshold, 2500))
      );
      const rate = Math.min(
        0.99,
        Math.max(0, configuredNumber(suppliedRules.combinationDiscountRate, 0.25))
      );
      const maxPerSet = Math.max(
        0,
        Math.round(configuredNumber(suppliedRules.combinationDiscountMaxPerSet, 800))
      );
      const maxOrderAmount = Math.max(
        0,
        Math.round(configuredNumber(suppliedRules.combinationDiscountMaxOrderAmount, 80000))
      );
      const protectedHighestPartPrice = partUnitPrices.reduce(
        (highest, price) => Math.max(highest, price),
        0
      );
      const additionalPartsPrice = Math.max(0, unitPrice - protectedHighestPartPrice);
      const excessPerSet = Math.max(0, unitPrice - threshold);
      // Only the value contributed by parts other than the highest-priced part
      // can generate a combination discount. This prevents a small added part
      // from unlocking a retroactive discount on an existing large part.
      const eligiblePerSet = Math.min(excessPerSet, additionalPartsPrice);
      const uncappedPerSet = Math.max(0, Math.round(eligiblePerSet * rate));
      const amountPerSet = enabled && partCount >= 2 ? Math.min(uncappedPerSet, maxPerSet) : 0;
      const uncappedAmount = amountPerSet * quantity;
      const amount = Math.min(uncappedAmount, maxOrderAmount);

      return {
        enabled,
        applied: amount > 0,
        unitPrice,
        quantity,
        partCount,
        threshold,
        rate,
        maxPerSet,
        maxOrderAmount,
        protectedHighestPartPrice,
        additionalPartsPrice,
        excessPerSet,
        eligiblePerSet,
        uncappedPerSet,
        amountPerSet,
        uncappedAmount,
        amount,
        capped: amountPerSet > 0 && (
          amountPerSet >= maxPerSet
          || uncappedAmount >= maxOrderAmount
        )
      };
    },

    normalizePartAreas(input, totalArea, mainArea, partCount) {
      if (Array.isArray(input.partAreas)) {
        const suppliedAreas = input.partAreas
          .map((area) => Math.max(0, Number(area) || 0))
          .filter((area) => area > 0);
        if (suppliedAreas.length > 0) return suppliedAreas;
      }
      if (totalArea <= 0) return [];
      if (partCount <= 1 || mainArea <= 0) return [totalArea];
      const primaryArea = Math.min(totalArea, mainArea);
      const remainingPartCount = Math.max(0, partCount - 1);
      const remainingArea = Math.max(0, totalArea - primaryArea);
      if (remainingPartCount === 0 || remainingArea === 0) return [primaryArea];
      const remainingPartArea = remainingArea / remainingPartCount;
      return [primaryArea, ...Array(remainingPartCount).fill(remainingPartArea)];
    },

    calculate(input, suppliedRules = {}) {
      const rules = {
        variableCostPerCm2: 1.4864,
        fixedLaborPerPart: 50,
        lowOrderThreshold: 2500,
        cncRate: 0,
        combinationDiscountEnabled: true,
        combinationDiscountThreshold: 2500,
        combinationDiscountRate: 0.25,
        combinationDiscountMaxPerSet: 800,
        combinationDiscountMaxOrderAmount: 80000,
        orderDiscountEnabled: false,
        orderDiscountStart: 9000,
        orderDiscountRateCapSubtotal: 100000,
        orderDiscountMidRate: 0.05,
        orderDiscountMaxSubtotal: 1000000,
        orderDiscountMaxRate: 0.08,
        orderDiscountMaxAmount: 80000,
        ...suppliedRules
      };
      const quantity = Math.max(1, Math.floor(Number(input.quantity) || 1));
      const suppliedTotalArea = Math.max(0, Number(input.totalArea) || 0);
      const suppliedMainArea = Math.max(0, Number(input.mainArea) || 0);
      const suppliedPartCount = Math.max(1, Math.floor(Number(input.partCount) || 1));
      const partAreas = this.normalizePartAreas(
        input,
        suppliedTotalArea,
        suppliedMainArea,
        suppliedPartCount
      );
      const totalArea = partAreas.reduce((sum, area) => sum + area, 0);
      const mainArea = partAreas.reduce((largest, area) => Math.max(largest, area), 0);
      const partCount = partAreas.length;
      const partPricing = partAreas.map((partArea, index) => {
        const base = this.calculatePartBase(quantity, partArea, rules);
        return {
          ...base,
          index: index + 1,
          unitPrice: Math.round(base.baseUnitRaw)
        };
      });
      const sumPartPricing = (key) => partPricing.reduce((sum, part) => sum + part[key], 0);
      const baseUnitRaw = sumPartPricing('baseUnitRaw');
      const cncUnitRaw = input.processingMethod === 'cnc' ? baseUnitRaw * rules.cncRate : 0;
      const baseUnitPrice = partPricing.reduce((sum, part) => sum + part.unitPrice, 0);
      const processingUnitPrice = Math.round(cncUnitRaw);
      const unitPrice = baseUnitPrice + processingUnitPrice;
      const subtotal = unitPrice * quantity;
      const partUnitPricesForCombination = partPricing.map((part) => {
        if (processingUnitPrice <= 0 || baseUnitPrice <= 0) return part.unitPrice;
        return part.unitPrice + (processingUnitPrice * (part.unitPrice / baseUnitPrice));
      });
      const combinationDiscount = this.calculateCombinationDiscount(
        unitPrice,
        quantity,
        partUnitPricesForCombination,
        rules
      );
      const orderDiscount = this.calculateOrderDiscount(subtotal, rules);
      const selectedDiscount = combinationDiscount.amount >= orderDiscount.amount
        ? {
          type: combinationDiscount.amount > 0 ? 'combination' : 'none',
          amount: combinationDiscount.amount
        }
        : {
          type: 'order',
          amount: orderDiscount.amount
        };
      const total = subtotal - selectedDiscount.amount;
      const oneUnitPartPricing = partAreas.map((partArea) => this.calculatePartBase(1, partArea, rules));
      const oneUnitBasePrice = oneUnitPartPricing.reduce(
        (sum, part) => sum + Math.round(part.baseUnitRaw),
        0
      );
      const oneUnitBaseRaw = oneUnitPartPricing.reduce((sum, part) => sum + part.baseUnitRaw, 0);
      const oneUnitProcessing = Math.round(
        input.processingMethod === 'cnc' ? oneUnitBaseRaw * rules.cncRate : 0
      );
      const oneUnitPrice = oneUnitBasePrice + oneUnitProcessing;
      const effectiveDiscountRate = oneUnitPrice > 0
        ? Math.max(0, 1 - (unitPrice / oneUnitPrice))
        : 0;

      return {
        rules,
        quantity,
        totalArea,
        mainArea,
        partCount,
        partAreas,
        partPricing,
        variableUnitCost: sumPartPricing('variableUnitCost'),
        fixedOrderLabor: sumPartPricing('fixedOrderLabor'),
        directUnitCost: sumPartPricing('directUnitCost'),
        directOrderCost: sumPartPricing('directOrderCost'),
        costQuoteUnit: sumPartPricing('costQuoteUnit'),
        preliminaryTotal: sumPartPricing('preliminaryTotal'),
        eightyMarginOrderPrice: sumPartPricing('eightyMarginOrderPrice'),
        lowAmountProtectionTotal: sumPartPricing('lowAmountProtectionTotal'),
        lowAmountProtectionUnit: sumPartPricing('lowAmountProtectionUnit'),
        smallAreaFloorUnit: sumPartPricing('smallAreaFloorUnit'),
        smallAreaProtectedUnit: sumPartPricing('smallAreaProtectedUnit'),
        baseUnitRaw,
        cncUnitRaw,
        baseUnitPrice,
        processingUnitPrice,
        unitPrice,
        oneUnitPrice,
        effectiveDiscountRate,
        productSubtotal: baseUnitPrice * quantity,
        subtotal,
        combinationDiscount,
        combinationDiscountApplied: selectedDiscount.type === 'combination',
        combinationDiscountAmount: combinationDiscount.amount,
        combinationDiscountAmountPerSet: combinationDiscount.amountPerSet,
        orderDiscount,
        orderDiscountApplied: selectedDiscount.type === 'order',
        orderDiscountAmount: selectedDiscount.amount,
        orderDiscountCandidateAmount: orderDiscount.amount,
        orderDiscountRate: subtotal > 0 ? selectedDiscount.amount / subtotal : 0,
        orderDiscountNominalRate: orderDiscount.nominalRate,
        orderDiscountCapped: selectedDiscount.type === 'combination'
          ? combinationDiscount.capped
          : orderDiscount.capped,
        selectedDiscountType: selectedDiscount.type,
        selectedDiscountAmount: selectedDiscount.amount,
        selectedDiscountCapped: selectedDiscount.type === 'combination'
          ? combinationDiscount.capped
          : orderDiscount.capped,
        total
      };
    }
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = PricingV1;
  if (typeof document === 'undefined') return;

  class AcrylicQuoteCalculator {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('[data-quote-form]');
      this.partsList = root.querySelector('[data-parts-list]');
      this.partTemplate = root.querySelector('[data-part-template]');
      this.processTemplate = root.querySelector('[data-process-template]');
      this.addButton = root.querySelector('[data-add-part]');
      this.copyButton = root.querySelector('[data-copy-quote]');
      this.orderLinks = Array.from(root.querySelectorAll('[data-order-link]'));
      this.orderLink = this.orderLinks[0];
      this.contactLink = root.querySelector('[data-contact-link]');
      this.partsCount = root.querySelector('[data-parts-count]');
      this.partsProgress = root.querySelector('[data-parts-progress]');
      this.partsHelp = root.querySelector('[data-parts-help]');
      this.copyStatus = root.querySelector('[data-copy-status]');
      this.validation = root.querySelector('[data-validation]');
      this.reviewIncompleteButton = root.querySelector('[data-review-incomplete]');
      this.productDescription = root.querySelector('[data-product-description]');
      this.draftPrompt = root.querySelector('[data-draft-prompt]');
      this.mobileSummary = root.querySelector('.acrylic-quote__mobile-summary');
      this.summaryRegion = root.querySelector('.acrylic-quote__summary');
      this.undoToast = root.querySelector('[data-undo-toast]');
      this.undoMessage = root.querySelector('[data-undo-message]');
      this.optionDialog = root.querySelector('[data-hole-dialog]');
      this.optionDialogTitle = root.querySelector('[data-hole-dialog-title]');
      this.optionDialogSize = root.querySelector('[data-hole-dialog-size]');
      this.optionDialogDescription = root.querySelector('[data-hole-dialog-description]');
      this.optionDialogNote = root.querySelector('[data-hole-dialog-note]');
      this.optionDialogFigure = root.querySelector('[data-hole-dialog-figure]');
      this.whiteGuideLink = root.querySelector('[data-white-guide-link]');
      this.optionDialogClose = root.querySelector('[data-hole-dialog-close]');
      this.fallbackOrderUrl = this.orderLink.getAttribute('href') || '#';
      this.fallbackContactUrl = this.contactLink.getAttribute('href') || '#';
      this.maximumParts = 10;
      this.maximumProcessesPerPart = 10;
      this.nextPartId = 1;
      this.nextProcessId = 1;
      this.lastEstimate = null;
      this.copyStatusTimer = null;
      this.partActionStatusTimer = null;
      this.priceFeedbackTimer = null;
      this.lastPriceKey = '';
      this.lastDialogTrigger = null;
      this.validationRequested = false;
      this.hasUserInteracted = false;
      this.isRestoringDraft = false;
      this.draftStorageKey = 'idworld:acrylic-quote-draft:v1';
      this.savedDraft = null;
      this.pendingRemovedPart = null;
      this.undoTimer = null;
      this.calculatorInViewport = false;
      this.summaryInViewport = false;

      const configElement = root.querySelector('[data-acrylic-quote-config]');
      this.config = JSON.parse(configElement.textContent);
      this.normalizeConfig();
      this.savedDraft = this.readSavedDraft();
      this.moneyFormatter = new Intl.NumberFormat(this.config.locale || 'ja-JP', {
        style: 'currency',
        currency: this.config.currency || 'JPY',
        maximumFractionDigits: 0
      });

      this.handleFormChange = this.handleFormChange.bind(this);
      this.handleSecondaryQuantityInput = this.handleSecondaryQuantityInput.bind(this);
      this.handleStepToggle = this.handleStepToggle.bind(this);
      this.handlePartsClick = this.handlePartsClick.bind(this);
      this.handleRootClick = this.handleRootClick.bind(this);
      this.addPart = this.addPart.bind(this);
      this.copyQuote = this.copyQuote.bind(this);
      this.prepareOrderHandoff = this.prepareOrderHandoff.bind(this);
      this.closeOptionDialog = this.closeOptionDialog.bind(this);
      this.handleDialogKeydown = this.handleDialogKeydown.bind(this);

      this.populateOptionSelects(this.root);
      this.populateOptionSelects(this.partTemplate.content);
      this.populateOptionRadios(this.root);
      this.populateOptionRadios(this.partTemplate.content);
      this.form.addEventListener('input', this.handleFormChange);
      this.form.addEventListener('change', this.handleFormChange);
      this.root.addEventListener('input', this.handleSecondaryQuantityInput);
      this.root.addEventListener('change', this.handleSecondaryQuantityInput);
      this.root.addEventListener('toggle', this.handleStepToggle, true);
      this.partsList.addEventListener('click', this.handlePartsClick);
      this.root.addEventListener('click', this.handleRootClick);
      this.addButton.addEventListener('click', this.addPart);
      this.copyButton.addEventListener('click', this.copyQuote);
      this.orderLinks.forEach((link) => link.addEventListener('click', this.prepareOrderHandoff));
      this.optionDialogClose.addEventListener('click', this.closeOptionDialog);
      this.optionDialog.addEventListener('click', (event) => {
        if (event.target === this.optionDialog) this.closeOptionDialog();
      });
      document.addEventListener('keydown', this.handleDialogKeydown);

      if (this.getParts().length === 0) {
        this.createPart({ initial: true });
      } else {
        this.updatePartControls();
        this.calculate();
      }
      this.updateDraftPrompt();
      this.initializeFloatingSummary();
    }

    getField(name) {
      return this.form.querySelector(`[data-field="${name}"]`);
    }

    getParts() {
      return Array.from(this.partsList.querySelectorAll('[data-part]'));
    }

    getProcessRows(part) {
      return Array.from(part.querySelectorAll('[data-process-row]'));
    }

    parseNumber(value) {
      const number = Number.parseFloat(value);
      return Number.isFinite(number) ? number : 0;
    }

    isPositiveInteger(value) {
      const number = Number(value);
      return Number.isInteger(number) && number >= 1;
    }

    formatMoney(value) {
      return this.moneyFormatter.format(Math.round(value));
    }

    formatPartTitle(number) {
      const circledNumbers = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];
      return `アクリルパーツ${circledNumbers[number - 1] || number}`;
    }

    normalizeConfig() {
      const configuredNumber = (value, fallback) => {
        if (value === null || value === undefined || value === '') return fallback;
        const number = Number.parseFloat(value);
        return Number.isFinite(number) ? number : fallback;
      };
      this.config.texts = this.config.texts || {};
      this.config.quoteValidityDays = Math.max(
        1,
        Math.floor(configuredNumber(this.config.quoteValidityDays, 15))
      );
      this.config.minimumDimension = Math.max(1, configuredNumber(this.config.minimumDimension, 20));
      this.config.maximumDimension = Math.max(
        this.config.minimumDimension,
        configuredNumber(this.config.maximumDimension, 400)
      );
      this.config.maximumPartArea = Math.max(1, configuredNumber(this.config.maximumPartArea, 1600));
      this.config.maximumQuantity = Math.max(
        1,
        Math.floor(configuredNumber(this.config.maximumQuantity, 1000))
      );
      this.config.variableCostPerCm2 = Math.max(
        0,
        configuredNumber(this.config.variableCostPerCm2, 1.4864)
      );
      this.config.fixedLaborPerPart = Math.max(
        0,
        configuredNumber(this.config.fixedLaborPerPart, 50)
      );
      this.config.lowOrderThreshold = Math.max(
        0,
        configuredNumber(this.config.lowOrderThreshold, 2500)
      );
      this.config.cncRate = Math.max(0, this.parseNumber(this.config.cncRate));
      this.config.combinationDiscountEnabled = this.config.combinationDiscountEnabled !== false
        && this.config.combinationDiscountEnabled !== 'false';
      this.config.combinationDiscountThreshold = Math.max(
        0,
        Math.round(configuredNumber(this.config.combinationDiscountThreshold, 2500))
      );
      this.config.combinationDiscountRate = Math.min(
        0.99,
        Math.max(0, configuredNumber(this.config.combinationDiscountRate, 0.25))
      );
      this.config.combinationDiscountMaxPerSet = Math.max(
        0,
        Math.round(configuredNumber(this.config.combinationDiscountMaxPerSet, 800))
      );
      this.config.combinationDiscountMaxOrderAmount = Math.max(
        0,
        Math.round(configuredNumber(this.config.combinationDiscountMaxOrderAmount, 80000))
      );
      this.config.orderDiscountEnabled = this.config.orderDiscountEnabled !== false
        && this.config.orderDiscountEnabled !== 'false';
      this.config.orderDiscountStart = Math.max(
        0,
        this.config.orderDiscountStart == null ? 9000 : this.parseNumber(this.config.orderDiscountStart)
      );
      this.config.orderDiscountRateCapSubtotal = Math.max(
        this.config.orderDiscountStart + 1,
        this.config.orderDiscountRateCapSubtotal == null
          ? 100000
          : this.parseNumber(this.config.orderDiscountRateCapSubtotal)
      );
      this.config.orderDiscountMidRate = Math.min(
        0.99,
        Math.max(
          0,
          this.config.orderDiscountMidRate == null ? 0.05 : this.parseNumber(this.config.orderDiscountMidRate)
        )
      );
      this.config.orderDiscountMaxSubtotal = Math.max(
        this.config.orderDiscountRateCapSubtotal + 1,
        this.config.orderDiscountMaxSubtotal == null
          ? 1000000
          : this.parseNumber(this.config.orderDiscountMaxSubtotal)
      );
      this.config.orderDiscountMaxRate = Math.min(
        0.99,
        Math.max(
          this.config.orderDiscountMidRate,
          this.config.orderDiscountMaxRate == null ? 0.08 : this.parseNumber(this.config.orderDiscountMaxRate)
        )
      );
      const terminalOrderDiscount = Math.round(
        this.config.orderDiscountMaxSubtotal * this.config.orderDiscountMaxRate
      );
      this.config.orderDiscountMaxAmount = Math.max(
        0,
        Math.min(
          terminalOrderDiscount,
          Math.round(
          this.config.orderDiscountMaxAmount == null
            ? terminalOrderDiscount
            : this.parseNumber(this.config.orderDiscountMaxAmount)
          )
        )
      );
      ['products', 'printSpecs', 'whiteGuides', 'holeSpecs'].forEach((collectionName) => {
        this.normalizeOptionCollection(collectionName);
      });
      this.normalizeProcessCategories();
    }

    normalizeOptionCollection(collectionName) {
      const source = this.config[collectionName] || [];
      const list = Array.isArray(source)
        ? source
        : Object.entries(source).map(([key, value]) => ({ key, ...value }));
      const cleanList = list
        .filter((item) => item && item.key)
        .map((item) => ({
          ...item,
          key: String(item.key),
          label: item.label || String(item.key)
        }));

      this.config[`${collectionName}List`] = cleanList;
      this.config[collectionName] = cleanList.reduce((map, item) => {
        map[item.key] = item;
        return map;
      }, {});
    }

    normalizeProcessCategories() {
      const seen = new Set();
      const categories = [];
      const normalized = this.config.holeSpecsList.map((item) => {
        const categoryKey = item.categoryKey || 'other';
        const categoryLabel = item.categoryLabel || 'その他加工';
        if (categoryKey !== 'none' && !seen.has(categoryKey)) {
          seen.add(categoryKey);
          categories.push({ key: categoryKey, label: categoryLabel });
        }
        return { ...item, categoryKey, categoryLabel };
      });

      this.config.holeSpecsList = normalized;
      this.config.holeSpecs = normalized.reduce((map, item) => {
        map[item.key] = item;
        return map;
      }, {});
      this.config.processCategoriesList = categories;
    }

    populateOptionSelects(scope) {
      scope.querySelectorAll('[data-option-source]').forEach((select) => {
        this.populateSelect(select);
      });
    }

    populateSelect(select) {
      const sourceName = select.dataset.optionSource;
      const list = this.config[`${sourceName}List`] || [];
      const currentValue = select.value;
      select.innerHTML = '';

      list.forEach((item) => {
        const option = document.createElement('option');
        option.value = item.key;
        option.textContent = item.label;
        select.appendChild(option);
      });

      if (currentValue && list.some((item) => item.key === currentValue)) select.value = currentValue;
    }

    populateOptionRadios(scope) {
      scope.querySelectorAll('[data-option-radio-source]').forEach((container) => {
        const sourceName = container.dataset.optionRadioSource;
        const list = this.config[`${sourceName}List`] || [];
        const selectedValue = container.querySelector('[data-field="print-spec"]:checked')?.value || '';
        const inputName = container.dataset.radioName || '';
        container.innerHTML = '';

        list.forEach((item) => {
          const label = document.createElement('label');
          label.className = 'acrylic-quote__radio-card acrylic-quote__radio-card--print';
          const input = document.createElement('input');
          input.type = 'radio';
          input.value = item.key;
          input.dataset.field = 'print-spec';
          if (inputName) input.name = inputName;
          if (selectedValue === item.key) input.checked = true;

          const text = document.createElement('span');
          const strong = document.createElement('strong');
          strong.textContent = item.label;
          const small = document.createElement('small');
          small.textContent = item.noPrint
            ? '印刷・白版なしの透明仕上げです。'
            : '白版とカットラインを続けて選択してください。';
          text.append(strong, small);
          label.append(input, text);
          container.appendChild(label);
        });
      });
    }

    addPlaceholder(select, label) {
      const option = document.createElement('option');
      option.value = '';
      option.textContent = label;
      option.selected = true;
      select.appendChild(option);
    }

    populateProcessCategorySelect(row) {
      const select = row.querySelector('[data-process-category]');
      const currentValue = select.value;
      select.innerHTML = '';
      this.addPlaceholder(select, '加工カテゴリを選択');
      this.config.processCategoriesList.forEach((category) => {
        const option = document.createElement('option');
        option.value = category.key;
        option.textContent = category.label;
        select.appendChild(option);
      });
      if (currentValue && this.config.processCategoriesList.some((item) => item.key === currentValue)) {
        select.value = currentValue;
      }
    }

    populateProcessSpecSelect(row, preserveValue = false) {
      const categorySelect = row.querySelector('[data-process-category]');
      const specSelect = row.querySelector('[data-process-spec]');
      const currentValue = preserveValue ? specSelect.value : '';
      const list = this.config.holeSpecsList.filter((item) => item.categoryKey === categorySelect.value);
      specSelect.innerHTML = '';
      this.addPlaceholder(specSelect, categorySelect.value ? '加工仕様を選択' : '先にカテゴリを選択');
      list.forEach((item) => {
        const option = document.createElement('option');
        option.value = item.key;
        option.textContent = item.label;
        specSelect.appendChild(option);
      });
      specSelect.disabled = !categorySelect.value;
      if (currentValue && list.some((item) => item.key === currentValue)) specSelect.value = currentValue;
      this.updateProcessRow(row);
    }

    getOption(collectionName, key, fallbackLabel) {
      const map = this.config[collectionName] || {};
      if (key && map[key]) return map[key];
      return {
        key: '',
        label: fallbackLabel,
        fee: 0,
        price: 0,
        orderUrl: this.fallbackOrderUrl
      };
    }

    shouldShowError(input, part, stepName) {
      if (this.validationRequested) return true;
      if (input && input.dataset.touched === 'true') return true;
      if (!part || !stepName) return false;
      const stateName = `validate${stepName.charAt(0).toUpperCase()}${stepName.slice(1)}`;
      return part.dataset[stateName] === 'true';
    }

    setInvalidState(element, invalid, visible) {
      if (!element) return;
      if (invalid && visible) {
        element.setAttribute('aria-invalid', 'true');
      } else {
        element.removeAttribute('aria-invalid');
      }
    }

    readProcesses(part) {
      return this.getProcessRows(part).map((row, index) => {
        const categoryInput = row.querySelector('[data-process-category]');
        const specInput = row.querySelector('[data-process-spec]');
        const option = this.getOption('holeSpecs', specInput.value, '加工未選択');
        const valid = Boolean(categoryInput.value && specInput.value && option.key);
        const categoryVisible = this.shouldShowError(categoryInput, part, 'process');
        const specVisible = this.shouldShowError(specInput, part, 'process')
          || this.shouldShowError(categoryInput, part, 'process');
        this.setInvalidState(categoryInput, !categoryInput.value, categoryVisible);
        this.setInvalidState(specInput, !specInput.value, specVisible);
        return {
          index: index + 1,
          key: option.key,
          categoryKey: option.categoryKey || categoryInput.value,
          categoryLabel: option.categoryLabel || '追加加工',
          label: option.label,
          shortLabel: option.shortLabel || option.label,
          valid
        };
      });
    }

    readPart(part, index) {
      const shapeInput = part.querySelector('[data-field="shape"]:checked');
      const widthInput = part.querySelector('[data-field="width"]');
      const heightInput = part.querySelector('[data-field="height"]');
      const diameterInput = part.querySelector('[data-field="diameter"]');
      const printSpecInput = part.querySelector('[data-field="print-spec"]:checked');
      const whitePlateInput = part.querySelector('[data-field="white-plate"]:checked');
      const whiteOffsetInput = part.querySelector('[data-field="white-offset"]:checked');
      const cutPathInput = part.querySelector('[data-field="cut-path"]:checked');
      const shape = shapeInput && shapeInput.value === 'circle' ? 'circle' : 'standard';
      const printSpec = this.getOption('printSpecs', printSpecInput ? printSpecInput.value : '', '印刷未設定');
      const noPrint = Boolean(printSpec.key && printSpec.noPrint);
      const width = this.parseNumber(widthInput.value);
      const height = this.parseNumber(heightInput.value);
      const diameter = this.parseNumber(diameterInput.value);
      const minimumDimension = this.config.minimumDimension;
      const maximumDimension = this.config.maximumDimension;
      const widthMinimumValid = width >= minimumDimension;
      const heightMinimumValid = height >= minimumDimension;
      const diameterMinimumValid = diameter >= minimumDimension;
      const widthStepValid = Number.isInteger(width);
      const heightStepValid = Number.isInteger(height);
      const diameterStepValid = Number.isInteger(diameter);
      const minimumSizeValid = shape === 'circle'
        ? diameterMinimumValid
        : widthMinimumValid && heightMinimumValid;
      const dimensionStepValid = shape === 'circle'
        ? diameterStepValid
        : widthStepValid && heightStepValid;
      const sizeValid = minimumSizeValid && dimensionStepValid;
      const withinAutoSize = shape === 'circle'
        ? diameter <= maximumDimension
        : width <= maximumDimension && height <= maximumDimension;
      const area = sizeValid
        ? (shape === 'circle' ? Math.PI * ((diameter / 2) ** 2) : width * height) / 100
        : 0;
      const withinAutoArea = area <= this.config.maximumPartArea;
      const whitePlate = noPrint ? 'none' : (whitePlateInput ? whitePlateInput.value : '');
      const whiteOffset = noPrint ? 'none' : (whiteOffsetInput ? whiteOffsetInput.value : '');
      const productionOptionsValid = Boolean(
        printSpecInput
        && cutPathInput
        && (noPrint || (whitePlateInput && whiteOffsetInput))
      );
      const processes = this.readProcesses(part);
      const processesValid = processes.every((item) => item.valid);
      this.setInvalidState(
        widthInput,
        shape === 'standard' && (!widthMinimumValid || !widthStepValid),
        this.shouldShowError(widthInput, part, 'size')
      );
      this.setInvalidState(
        heightInput,
        shape === 'standard' && (!heightMinimumValid || !heightStepValid),
        this.shouldShowError(heightInput, part, 'size')
      );
      this.setInvalidState(
        diameterInput,
        shape === 'circle' && (!diameterMinimumValid || !diameterStepValid),
        this.shouldShowError(diameterInput, part, 'size')
      );
      const printOptions = part.querySelector('[data-option-radio-source="printSpecs"]');
      const intakeValidationVisible = this.validationRequested
        || part.dataset.validateIntake === 'true';
      this.setInvalidState(printOptions, !printSpecInput, intakeValidationVisible);
      const whiteOptions = part.querySelector('[data-option-section="white-plate"]');
      this.setInvalidState(
        whiteOptions,
        Boolean(printSpecInput && !noPrint && !whitePlateInput),
        intakeValidationVisible
      );

      return {
        index: index + 1,
        shape,
        width,
        height,
        diameter,
        area,
        printSpecKey: printSpec.key,
        noPrint,
        whitePlate,
        whiteOffset,
        cutPath: cutPathInput ? cutPathInput.value : '',
        processes,
        minimumSizeValid,
        dimensionStepValid,
        sizeValid,
        withinAutoSize,
        withinAutoArea,
        productionOptionsValid,
        processesValid,
        valid: sizeValid && productionOptionsValid && processesValid
      };
    }

    calculate() {
      const quantityInput = this.getField('quantity');
      const rawQuantity = Number(quantityInput.value);
      const quantityValid = this.isPositiveInteger(rawQuantity);
      const quantity = quantityValid ? rawQuantity : 0;
      this.setInvalidState(
        quantityInput,
        !quantityValid,
        this.validationRequested || quantityInput.dataset.touched === 'true'
      );
      const productKey = this.getField('product-type').value;
      const processingMethod = this.getRadioValue(this.form, 'processing-method') || 'regular';
      const product = this.getOption('products', productKey, '商品未設定');
      const partElements = this.getParts();
      const parts = partElements.map((part, index) => this.readPart(part, index));
      const cncNote = this.root.querySelector('[data-cnc-note]');
      if (cncNote) {
        const cncSelected = processingMethod === 'cnc';
        cncNote.classList.toggle('is-visible', cncSelected);
        cncNote.setAttribute('aria-hidden', String(!cncSelected));
      }
      if (this.productDescription) this.productDescription.textContent = product.description || '';
      const sizedParts = parts.filter((part) => part.sizeValid);
      const totalArea = sizedParts.reduce((sum, part) => sum + part.area, 0);
      const pricing = PricingV1.calculate({
        quantity: quantity || 1,
        totalArea,
        partAreas: sizedParts.map((part) => part.area),
        partCount: sizedParts.length,
        processingMethod
      }, {
        variableCostPerCm2: this.config.variableCostPerCm2,
        fixedLaborPerPart: this.config.fixedLaborPerPart,
        lowOrderThreshold: this.config.lowOrderThreshold,
        cncRate: this.config.cncRate,
        combinationDiscountEnabled: this.config.combinationDiscountEnabled,
        combinationDiscountThreshold: this.config.combinationDiscountThreshold,
        combinationDiscountRate: this.config.combinationDiscountRate,
        combinationDiscountMaxPerSet: this.config.combinationDiscountMaxPerSet,
        combinationDiscountMaxOrderAmount: this.config.combinationDiscountMaxOrderAmount,
        orderDiscountEnabled: this.config.orderDiscountEnabled,
        orderDiscountStart: this.config.orderDiscountStart,
        orderDiscountRateCapSubtotal: this.config.orderDiscountRateCapSubtotal,
        orderDiscountMidRate: this.config.orderDiscountMidRate,
        orderDiscountMaxSubtotal: this.config.orderDiscountMaxSubtotal,
        orderDiscountMaxRate: this.config.orderDiscountMaxRate,
        orderDiscountMaxAmount: this.config.orderDiscountMaxAmount
      });
      const errors = [];
      const visibleErrors = [];
      const manualReasons = [];

      if (!quantityValid) {
        const message = '数量は1以上の整数で入力してください。';
        errors.push(message);
        if (this.validationRequested || quantityInput.dataset.touched === 'true') visibleErrors.push(message);
      }
      if (quantityValid && quantity > this.config.maximumQuantity) {
        manualReasons.push(`数量が自動見積もり上限の${this.config.maximumQuantity}個を超えています。`);
      }
      parts.forEach((part, index) => {
        const partElement = partElements[index];
        const title = this.formatPartTitle(part.index);
        const sizeErrorVisible = this.validationRequested
          || partElement.dataset.validateSize === 'true'
          || Array.from(partElement.querySelectorAll('[data-field="width"], [data-field="height"], [data-field="diameter"]'))
            .some((input) => input.dataset.touched === 'true' && input.hasAttribute('aria-invalid'));
        const intakeErrorVisible = this.validationRequested
          || partElement.dataset.validateIntake === 'true';
        const processErrorVisible = this.validationRequested
          || partElement.dataset.validateProcess === 'true'
          || Array.from(partElement.querySelectorAll('[data-process-row] select'))
            .some((input) => input.dataset.touched === 'true');
        if (!part.minimumSizeValid) {
          const message = `${title}：${this.config.texts.missingParts || `各辺または直径を${this.config.minimumDimension}mm以上で入力してください。`}`;
          errors.push(message);
          if (sizeErrorVisible) visibleErrors.push(message);
        } else if (!part.dimensionStepValid) {
          const message = `${title}：${this.config.texts.dimensionStep || 'サイズは1mm単位の整数で入力してください。'}`;
          errors.push(message);
          if (sizeErrorVisible) visibleErrors.push(message);
        } else if (!part.withinAutoSize) {
          manualReasons.push(`${title}のサイズが自動見積もり上限の${this.config.maximumDimension}mmを超えています。`);
        }
        if (part.sizeValid && !part.withinAutoArea) {
          manualReasons.push(`${title}の計算面積が1パーツあたりの自動見積もり上限${this.config.maximumPartArea}cm²を超えています。`);
        }
        if (!part.printSpecKey) {
          const message = `${title}の印刷仕様を選択してください。`;
          errors.push(message);
          if (intakeErrorVisible) visibleErrors.push(message);
        } else if (!part.productionOptionsValid) {
          const message = part.noPrint
            ? `${title}のカットラインを選択してください。`
            : `${title}の白版・縮小設定・カットラインを選択してください。`;
          errors.push(message);
          if (intakeErrorVisible) visibleErrors.push(message);
        }
        if (!part.processesValid) {
          const message = `${title}の追加加工を選択するか、不要な行を削除してください。`;
          errors.push(message);
          if (processErrorVisible) visibleErrors.push(message);
        }
      });

      const inputValid = errors.length === 0;
      const manualInquiry = inputValid && manualReasons.length > 0;
      const orderable = inputValid && !manualInquiry;
      const finalUnitPrice = quantity > 0 ? pricing.total / quantity : 0;

      this.lastEstimate = {
        quantity,
        quantityValid,
        productKey,
        product,
        processingMethod,
        parts,
        totalArea,
        pricing,
        discountRate: pricing.effectiveDiscountRate,
        productSubtotal: pricing.productSubtotal,
        unitPrice: pricing.unitPrice,
        finalUnitPrice,
        subtotal: pricing.subtotal,
        combinationDiscountApplied: pricing.combinationDiscountApplied,
        combinationDiscountAmount: pricing.combinationDiscountAmount,
        combinationDiscountAmountPerSet: pricing.combinationDiscountAmountPerSet,
        orderDiscountApplied: pricing.orderDiscountApplied,
        orderDiscountAmount: pricing.orderDiscountAmount,
        orderDiscountRate: pricing.orderDiscountRate,
        orderDiscountCapped: pricing.orderDiscountCapped,
        total: pricing.total,
        errors,
        visibleErrors,
        manualReasons,
        inputValid,
        manualInquiry,
        orderable,
        valid: orderable
      };

      this.renderEstimate();
      if (this.hasUserInteracted && !this.isRestoringDraft) this.saveDraft();
    }

    renderEstimate() {
      const estimate = this.lastEstimate;
      if (estimate.manualInquiry) {
        this.setOutput('unit-price', '個別見積もり');
        this.setOutput('total', '要確認');
      } else if (!estimate.inputValid) {
        this.setOutput('unit-price', '—');
        this.setOutput('total', '—');
      } else {
        this.setOutput('unit-price', this.formatMoney(estimate.finalUnitPrice));
        this.setOutput('total', this.formatMoney(estimate.total));
      }
      this.setOutput('current-quantity', estimate.quantityValid ? `${estimate.quantity}個` : '—');
      this.root.querySelectorAll('[data-quantity-preset]').forEach((button) => {
        const active = estimate.quantityValid && Number(button.dataset.quantityPreset) === estimate.quantity;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      const quantityValue = this.getField('quantity').value;
      this.root.querySelectorAll('[data-quantity-input-secondary]').forEach((input) => {
        if (input.value !== quantityValue) input.value = quantityValue;
      });
      const baseOrderUrl = estimate.product.orderUrl || this.fallbackOrderUrl;
      let resolvedOrderUrl = baseOrderUrl;
      if (estimate.orderable) {
        try {
          const targetUrl = new URL(baseOrderUrl, window.location.href);
          targetUrl.searchParams.set('from_quote', '1');
          targetUrl.searchParams.set('aq_message', this.buildOrderMessage());
          resolvedOrderUrl = targetUrl.toString();
        } catch (error) {
          resolvedOrderUrl = baseOrderUrl;
        }
      }
      this.orderLinks.forEach((link) => {
        link.href = resolvedOrderUrl;
      });
      this.contactLink.href = this.config.contactUrl || this.fallbackContactUrl;
      this.validation.textContent = estimate.manualInquiry
        ? `自動見積もり範囲外です。個別見積もりについてお問い合わせください。 ${estimate.manualReasons.join(' ')}`
        : estimate.visibleErrors.slice(0, 3).join(' ');
      this.root.classList.toggle('is-manual-inquiry', estimate.manualInquiry);
      this.copyButton.disabled = false;
      this.copyButton.removeAttribute('aria-disabled');
      if (this.reviewIncompleteButton) {
        this.reviewIncompleteButton.hidden = estimate.inputValid
          || (!this.validationRequested && estimate.visibleErrors.length === 0);
      }
      this.orderLinks.forEach((link) => {
        link.classList.toggle('is-disabled', !estimate.orderable);
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      });
      let completedParts = 0;
      this.getParts().forEach((partElement, index) => {
        const part = estimate.parts[index];
        if (part && part.valid) completedParts += 1;
        this.updatePartPresentation(partElement, part);
        this.updateProcessControls(partElement);
      });
      if (this.partsProgress) {
        this.partsProgress.textContent = `入力完了：${completedParts}／${estimate.parts.length}パーツ`;
      }
      const priceKey = estimate.inputValid && !estimate.manualInquiry
        ? `${estimate.quantity}:${estimate.finalUnitPrice}:${estimate.total}`
        : '';
      if (this.lastPriceKey && priceKey && priceKey !== this.lastPriceKey) this.showPriceFeedback();
      this.lastPriceKey = priceKey;
      this.updateFloatingSummaryVisibility();
    }

    updatePartPresentation(partElement, part) {
      if (!partElement || !part) return;
      const status = partElement.querySelector('[data-part-status]');
      const summary = partElement.querySelector('[data-part-summary]');
      const sizeSummary = partElement.querySelector('[data-size-summary]');
      const intakeSummary = partElement.querySelector('[data-intake-summary]');
      const sizeDetails = partElement.querySelector('[data-size-details]');
      const intakeDetails = partElement.querySelector('[data-intake-details]');
      const processDetails = partElement.querySelector('[data-process-details]');
      const printSpec = this.getOption('printSpecs', part.printSpecKey, '印刷未選択');
      const processLabel = part.processes.length > 0 ? `加工指定${part.processes.length}件` : '加工指定なし';
      const sizeLabel = part.shape === 'circle'
        ? `円形・直径${part.diameter || '—'}mm`
        : `横×縦・${part.width || '—'}×${part.height || '—'}mm`;
      const whiteLabel = part.noPrint
        ? '白版なし'
        : (part.whitePlate ? this.getWhitePlateLabel(part.whitePlate) : '白版未選択');
      const needsManualQuote = part.sizeValid && (!part.withinAutoSize || !part.withinAutoArea);
      if (summary) {
        const missingLabels = [];
        if (!part.sizeValid) missingLabels.push('サイズ未入力');
        if (!part.printSpecKey) missingLabels.push('印刷未選択');
        summary.textContent = missingLabels.length > 0
          ? missingLabels.join('／')
          : `${sizeLabel}／${printSpec.shortLabel || printSpec.label}／${processLabel}／${part.area.toFixed(2)}cm²`;
      }
      if (sizeSummary) sizeSummary.textContent = part.sizeValid ? `${sizeLabel.replace('横×縦・', '').replace('円形・', '')} ✓` : '未入力';
      if (intakeSummary) {
        intakeSummary.textContent = part.printSpecKey && part.productionOptionsValid
          ? `${printSpec.shortLabel || printSpec.label}・${whiteLabel} ✓`
          : part.printSpecKey
            ? `${printSpec.shortLabel || printSpec.label}・要確認`
          : '印刷仕様を選択';
      }
      this.setStepState(sizeDetails, part.sizeValid);
      this.setStepState(intakeDetails, Boolean(part.printSpecKey && part.productionOptionsValid));
      this.setStepState(processDetails, part.processesValid);
      if (status) {
        status.classList.remove('is-complete', 'is-incomplete', 'is-manual');
        if (needsManualQuote) {
          status.textContent = '個別見積もり';
          status.classList.add('is-manual');
        } else if (part.valid) {
          status.textContent = '入力完了';
          status.classList.add('is-complete');
        } else {
          status.textContent = '要確認';
          status.classList.add('is-incomplete');
        }
      }
    }

    setPartExpanded(part, expanded) {
      if (!part) return;
      const toggle = part.querySelector('[data-toggle-part]');
      const body = part.querySelector('[data-part-body]');
      part.classList.toggle('is-expanded', expanded);
      part.classList.toggle('is-collapsed', !expanded);
      if (toggle) {
        toggle.setAttribute('aria-expanded', String(expanded));
        const title = part.querySelector('[data-part-title]')?.textContent || 'アクリルパーツ';
        toggle.setAttribute('aria-label', `${title}の入力欄を${expanded ? '閉じる' : '開く'}`);
      }
      if (body) body.hidden = !expanded;
    }

    setStepState(details, complete) {
      if (!details) return;
      details.classList.toggle('is-complete', Boolean(complete));
      details.classList.toggle('is-incomplete', !complete);
    }

    showPriceFeedback() {
      const regions = this.root.querySelectorAll('[data-price-region], .acrylic-quote__current-quote, .acrylic-quote__mobile-total');
      regions.forEach((region) => region.classList.remove('is-price-updated'));
      window.requestAnimationFrame(() => {
        regions.forEach((region) => region.classList.add('is-price-updated'));
      });
      window.clearTimeout(this.priceFeedbackTimer);
      this.priceFeedbackTimer = window.setTimeout(() => {
        regions.forEach((region) => region.classList.remove('is-price-updated'));
      }, 650);
    }

    initializeFloatingSummary() {
      if (!this.mobileSummary || !this.summaryRegion) return;
      const refreshFromRects = () => {
        const rootRect = this.root.getBoundingClientRect();
        const summaryRect = this.summaryRegion.getBoundingClientRect();
        this.calculatorInViewport = rootRect.bottom > 0 && rootRect.top < window.innerHeight;
        this.summaryInViewport = summaryRect.bottom > 0 && summaryRect.top < window.innerHeight;
        this.updateFloatingSummaryVisibility();
      };

      if ('IntersectionObserver' in window) {
        this.calculatorObserver = new IntersectionObserver((entries) => {
          this.calculatorInViewport = entries.some((entry) => entry.isIntersecting);
          this.updateFloatingSummaryVisibility();
        }, { threshold: 0 });
        this.summaryObserver = new IntersectionObserver((entries) => {
          this.summaryInViewport = entries.some((entry) => entry.isIntersecting);
          this.updateFloatingSummaryVisibility();
        }, { threshold: 0.05 });
        this.calculatorObserver.observe(this.root);
        this.summaryObserver.observe(this.summaryRegion);
      } else {
        window.addEventListener('scroll', refreshFromRects, { passive: true });
      }
      window.addEventListener('resize', refreshFromRects, { passive: true });
      refreshFromRects();
    }

    updateFloatingSummaryVisibility() {
      if (!this.mobileSummary) return;
      const compactLayout = window.matchMedia
        ? window.matchMedia('(max-width: 1100px)').matches
        : window.innerWidth <= 1100;
      const dialogOpen = this.optionDialog && !this.optionDialog.hidden;
      const hasQuoteResult = Boolean(this.lastEstimate && this.lastEstimate.orderable);
      const visible = compactLayout
        && hasQuoteResult
        && this.calculatorInViewport
        && !this.summaryInViewport
        && !dialogOpen;
      this.mobileSummary.classList.toggle('is-visible', Boolean(visible));
      this.mobileSummary.setAttribute('aria-hidden', String(!visible));
    }

    setOutput(name, value) {
      this.root.querySelectorAll(`[data-output="${name}"]`).forEach((output) => {
        output.textContent = value;
      });
    }

    getScrollBehavior() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
    }

    markUserInteraction() {
      this.hasUserInteracted = true;
      if (this.draftPrompt && !this.draftPrompt.hidden) {
        this.draftPrompt.hidden = true;
        this.savedDraft = null;
      }
    }

    requestFullValidation() {
      this.validationRequested = true;
      this.getParts().forEach((part) => {
        part.dataset.validateSize = 'true';
        part.dataset.validateIntake = 'true';
        part.dataset.validateProcess = 'true';
      });
    }

    readSavedDraft() {
      try {
        const draft = JSON.parse(window.sessionStorage.getItem(this.draftStorageKey) || 'null');
        if (!draft || draft.version !== 1 || !Array.isArray(draft.parts) || draft.parts.length === 0) {
          return null;
        }
        return draft;
      } catch (error) {
        return null;
      }
    }

    updateDraftPrompt() {
      if (!this.draftPrompt) return;
      this.draftPrompt.hidden = !this.savedDraft;
    }

    discardSavedDraft() {
      try {
        window.sessionStorage.removeItem(this.draftStorageKey);
      } catch (error) {
        // The calculator remains usable when storage is unavailable.
      }
      this.savedDraft = null;
      this.updateDraftPrompt();
      this.showCopyStatus('前回の入力内容を破棄しました。');
    }

    serializeDraft() {
      return {
        version: 1,
        savedAt: Date.now(),
        productKey: this.getField('product-type').value,
        processingMethod: this.getRadioValue(this.form, 'processing-method') || 'regular',
        quantity: this.getField('quantity').value,
        parts: this.getParts().map((part) => ({
          shape: this.getRadioValue(part, 'shape'),
          width: part.querySelector('[data-field="width"]')?.value || '',
          height: part.querySelector('[data-field="height"]')?.value || '',
          diameter: part.querySelector('[data-field="diameter"]')?.value || '',
          printSpec: this.getRadioValue(part, 'print-spec'),
          whitePlate: this.getRadioValue(part, 'white-plate'),
          whiteOffset: this.getRadioValue(part, 'white-offset'),
          cutPath: this.getRadioValue(part, 'cut-path'),
          processes: this.getProcessRows(part).map((row) => ({
            category: row.querySelector('[data-process-category]')?.value || '',
            spec: row.querySelector('[data-process-spec]')?.value || ''
          }))
        }))
      };
    }

    saveDraft() {
      try {
        const draft = this.serializeDraft();
        window.sessionStorage.setItem(this.draftStorageKey, JSON.stringify(draft));
        this.savedDraft = draft;
      } catch (error) {
        // Storage can be blocked in strict privacy modes; calculation still works.
      }
    }

    applyDraftPart(part, preset = {}) {
      const shape = preset.shape === 'circle' ? 'circle' : 'standard';
      this.setRadioValue(part, 'shape', shape);
      ['width', 'height', 'diameter'].forEach((fieldName) => {
        const input = part.querySelector(`[data-field="${fieldName}"]`);
        if (input) input.value = preset[fieldName] == null ? '' : String(preset[fieldName]);
      });
      this.setRadioValue(part, 'print-spec', this.config.printSpecs[preset.printSpec] ? preset.printSpec : '');
      this.setRadioValue(part, 'white-plate', preset.whitePlate || '');
      this.setRadioValue(part, 'white-offset', preset.whiteOffset || 'auto');
      this.setRadioValue(part, 'cut-path', preset.cutPath || 'edge');
      const processList = part.querySelector('[data-process-list]');
      if (processList) processList.innerHTML = '';
      (Array.isArray(preset.processes) ? preset.processes : [])
        .slice(0, this.maximumProcessesPerPart)
        .forEach((process) => this.createProcessRow(part, process));
      this.updatePartShapeFields(part);
      this.updatePartPrintOptions(part);
      this.updateProcessControls(part);
    }

    restoreSavedDraft() {
      const draft = this.savedDraft || this.readSavedDraft();
      if (!draft) return;
      this.isRestoringDraft = true;
      this.validationRequested = false;
      this.partsList.innerHTML = '';
      const productInput = this.getField('product-type');
      const quantityInput = this.getField('quantity');
      if (this.config.products[draft.productKey]) productInput.value = draft.productKey;
      this.setRadioValue(
        this.form,
        'processing-method',
        draft.processingMethod === 'cnc' ? 'cnc' : 'regular'
      );
      quantityInput.value = draft.quantity == null ? '1' : String(draft.quantity);
      draft.parts.slice(0, this.maximumParts).forEach((preset) => {
        this.createPart({ initial: true, preset, skipCalculate: true, focus: false });
      });
      if (this.getParts().length === 0) {
        this.createPart({ initial: true, skipCalculate: true, focus: false });
      }
      this.updatePartControls();
      this.calculate();
      this.isRestoringDraft = false;
      this.hasUserInteracted = true;
      this.saveDraft();
      this.savedDraft = null;
      this.updateDraftPrompt();
      this.showCopyStatus('前回の入力内容を復元しました。');
      this.getParts()[0]?.querySelector('[data-toggle-part]')?.focus({ preventScroll: true });
    }

    handleFormChange(event) {
      if (!event.target.matches('[data-field]')) return;
      const changeOnlyControl = event.target.matches('select, input[type="radio"], input[type="checkbox"]');
      if (event.type === 'input' && changeOnlyControl) return;
      event.target.dataset.touched = 'true';
      this.markUserInteraction();
      if (event.target.matches('[data-field="shape"]')) {
        this.updatePartShapeFields(event.target.closest('[data-part]'));
      }
      if (event.target.matches('[data-field="print-spec"], [data-field="white-plate"]')) {
        this.updatePartPrintOptions(event.target.closest('[data-part]'));
      }
      if (event.target.matches('[data-field="process-category"]')) {
        const row = event.target.closest('[data-process-row]');
        this.populateProcessSpecSelect(row);
      }
      if (event.target.matches('[data-field="process-spec"]')) {
        this.updateProcessRow(event.target.closest('[data-process-row]'));
      }
      this.calculate();
    }

    handleSecondaryQuantityInput(event) {
      if (!event.target.matches('[data-quantity-input-secondary]')) return;
      const quantityInput = this.getField('quantity');
      quantityInput.value = event.target.value;
      quantityInput.dataset.touched = 'true';
      this.markUserInteraction();
      this.calculate();
    }

    handleStepToggle(event) {
      const details = event.target.closest('[data-step-details]');
      if (!details || !details.open) return;
      const part = details.closest('[data-part]');
      part.querySelectorAll('[data-step-details]').forEach((other) => {
        if (other !== details) other.open = false;
      });
    }

    handleRootClick(event) {
      if (event.target.closest('[data-restore-draft]')) {
        this.restoreSavedDraft();
        return;
      }
      if (event.target.closest('[data-discard-draft]')) {
        this.discardSavedDraft();
        return;
      }
      if (event.target.closest('[data-undo-remove]')) {
        this.restoreRemovedPart();
        return;
      }
      const presetButton = event.target.closest('[data-quantity-preset]');
      if (presetButton) {
        const quantityInput = this.getField('quantity');
        quantityInput.value = presetButton.dataset.quantityPreset;
        quantityInput.dataset.touched = 'true';
        this.markUserInteraction();
        this.calculate();
        return;
      }
      if (event.target.closest('[data-quantity-focus]')) {
        this.focusQuantityInput();
        return;
      }
      if (event.target.closest('[data-review-incomplete]')) {
        this.requestFullValidation();
        this.calculate();
        this.focusFirstIncomplete();
      }
    }

    focusQuantityInput() {
      const quantityInput = this.getField('quantity');
      quantityInput.scrollIntoView({ behavior: this.getScrollBehavior(), block: 'center' });
      window.setTimeout(() => {
        quantityInput.focus({ preventScroll: true });
        quantityInput.select();
      }, 250);
    }

    focusFirstIncomplete() {
      if (!this.lastEstimate) return;
      if (!this.lastEstimate.quantityValid) {
        this.focusQuantityInput();
        return;
      }
      const partIndex = this.lastEstimate.parts.findIndex((part) => !part.valid);
      const part = this.getParts()[partIndex];
      if (!part) return;
      this.setPartExpanded(part, true);
      const invalid = part.querySelector('[aria-invalid="true"]');
      let details = invalid ? invalid.closest('details') : null;
      const partEstimate = this.lastEstimate.parts[partIndex];
      if (!details && partEstimate) {
        if (!partEstimate.sizeValid) details = part.querySelector('[data-size-details]');
        else if (!partEstimate.printSpecKey || !partEstimate.productionOptionsValid) details = part.querySelector('[data-intake-details]');
        else if (!partEstimate.processesValid) details = part.querySelector('[data-process-details]');
      }
      if (details) details.open = true;
      const focusTarget = invalid && invalid.matches('input, select, button')
        ? invalid
        : invalid?.querySelector('input, select, button') || part.querySelector('input, select, button');
      const scrollTarget = invalid || focusTarget || part;
      scrollTarget.scrollIntoView({ behavior: this.getScrollBehavior(), block: 'center' });
      window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 250);
    }

    handlePartsClick(event) {
      const nextStepButton = event.target.closest('[data-next-step]');
      if (nextStepButton) {
        this.markUserInteraction();
        this.advancePartStep(nextStepButton.closest('[data-part]'), nextStepButton.dataset.nextStep);
        return;
      }
      const toggleButton = event.target.closest('[data-toggle-part]');
      if (toggleButton) {
        const part = toggleButton.closest('[data-part]');
        this.setPartExpanded(part, toggleButton.getAttribute('aria-expanded') !== 'true');
        return;
      }
      const copyPreviousButton = event.target.closest('[data-copy-previous]');
      if (copyPreviousButton) {
        this.markUserInteraction();
        this.copyPreviousSpecifications(copyPreviousButton.closest('[data-part]'));
        return;
      }
      const duplicateButton = event.target.closest('[data-duplicate-part]');
      if (duplicateButton) {
        this.markUserInteraction();
        this.createPart({ copyFrom: duplicateButton.closest('[data-part]'), copyDimensions: true });
        return;
      }
      const applyIntakeButton = event.target.closest('[data-apply-intake]');
      if (applyIntakeButton) {
        this.markUserInteraction();
        this.applyIntakeToAll(applyIntakeButton.closest('[data-part]'));
        return;
      }
      const helpButton = event.target.closest('[data-process-help]');
      if (helpButton) {
        this.openProcessDialog(helpButton);
        return;
      }
      const whiteGuideButton = event.target.closest('[data-white-guide]');
      if (whiteGuideButton) {
        this.openWhiteGuide(whiteGuideButton);
        return;
      }
      const addProcessButton = event.target.closest('[data-add-process]');
      if (addProcessButton) {
        this.markUserInteraction();
        this.addProcess(addProcessButton.closest('[data-part]'));
        return;
      }
      const removeProcessButton = event.target.closest('[data-remove-process]');
      if (removeProcessButton) {
        this.markUserInteraction();
        const part = removeProcessButton.closest('[data-part]');
        removeProcessButton.closest('[data-process-row]').remove();
        this.updateProcessControls(part);
        this.calculate();
        return;
      }
      const removeButton = event.target.closest('[data-remove-part]');
      if (!removeButton) return;
      const parts = this.getParts();
      if (parts.length <= 1) return;
      this.markUserInteraction();
      this.removePart(removeButton.closest('[data-part]'));
    }

    isPartEmpty(part) {
      const hasDimensions = ['width', 'height', 'diameter'].some((fieldName) => {
        return Boolean(part.querySelector(`[data-field="${fieldName}"]`)?.value.trim());
      });
      const hasPrintSpecification = Boolean(part.querySelector('[data-field="print-spec"]:checked'));
      const changedShape = this.getRadioValue(part, 'shape') !== 'standard';
      const changedCutPath = this.getRadioValue(part, 'cut-path') !== 'edge';
      const hasWhitePlate = Boolean(this.getRadioValue(part, 'white-plate'));
      return !hasDimensions
        && !hasPrintSpecification
        && !changedShape
        && !changedCutPath
        && !hasWhitePlate
        && this.getProcessRows(part).length === 0;
    }

    clearUndoToast() {
      window.clearTimeout(this.undoTimer);
      this.pendingRemovedPart = null;
      if (this.undoToast) this.undoToast.hidden = true;
      if (this.undoMessage) this.undoMessage.textContent = '';
    }

    removePart(part) {
      if (!part || this.getParts().length <= 1) return;
      const index = this.getParts().indexOf(part);
      const title = part.querySelector('[data-part-title]')?.textContent || 'アクリルパーツ';
      if (this.isPartEmpty(part)) {
        part.remove();
        this.updatePartControls();
        this.calculate();
        return;
      }
      this.clearUndoToast();
      this.pendingRemovedPart = { part, index };
      part.remove();
      this.updatePartControls();
      this.calculate();
      if (this.undoMessage) this.undoMessage.textContent = `${title}を削除しました。`;
      if (this.undoToast) this.undoToast.hidden = false;
      this.undoTimer = window.setTimeout(() => this.clearUndoToast(), 6000);
    }

    restoreRemovedPart() {
      if (!this.pendingRemovedPart) return;
      const { part, index } = this.pendingRemovedPart;
      const currentParts = this.getParts();
      this.partsList.insertBefore(part, currentParts[index] || null);
      this.clearUndoToast();
      this.updatePartControls();
      this.calculate();
      this.showCopyStatus('削除したアクリルパーツを元に戻しました。');
      part.querySelector('[data-toggle-part]')?.focus({ preventScroll: true });
    }

    advancePartStep(part, targetStep) {
      if (!part) return;
      if (targetStep === 'intake') part.dataset.validateSize = 'true';
      if (targetStep === 'process') part.dataset.validateIntake = 'true';
      this.calculate();
      const partIndex = this.getParts().indexOf(part);
      const estimate = this.lastEstimate && this.lastEstimate.parts[partIndex];
      if (!estimate) return;
      let target = null;
      if (targetStep === 'intake') {
        if (!estimate.sizeValid) {
          target = part.querySelector('[data-size-details]');
        } else {
          target = part.querySelector('[data-intake-details]');
        }
      } else if (targetStep === 'process') {
        if (!estimate.printSpecKey || !estimate.productionOptionsValid) {
          target = part.querySelector('[data-intake-details]');
        } else {
          target = part.querySelector('[data-process-details]');
        }
      }
      if (!target) return;
      part.querySelectorAll('[data-step-details]').forEach((details) => {
        if (details !== target) details.open = false;
      });
      target.open = true;
      const hasInvalidTarget = (targetStep === 'intake' && !estimate.sizeValid)
        || (targetStep === 'process' && (!estimate.printSpecKey || !estimate.productionOptionsValid));
      const focusTarget = hasInvalidTarget
        ? target.querySelector('[aria-invalid="true"]')
        : target.querySelector('summary');
      window.requestAnimationFrame(() => {
        target.scrollIntoView({ behavior: this.getScrollBehavior(), block: 'nearest' });
        window.setTimeout(() => focusTarget?.focus({ preventScroll: true }), 220);
      });
    }

    assignPartFieldIds(part, partId) {
      const sectionId = this.root.id.replace('AcrylicQuote-', '');
      const fields = [
        ['width', 'width', `AcrylicPartWidth-${sectionId}-${partId}`],
        ['height', 'height', `AcrylicPartHeight-${sectionId}-${partId}`],
        ['diameter', 'diameter', `AcrylicPartDiameter-${sectionId}-${partId}`]
      ];
      fields.forEach(([fieldName, labelName, id]) => {
        const input = part.querySelector(`[data-field="${fieldName}"]`);
        const label = part.querySelector(`[data-label-for="${labelName}"]`);
        input.id = id;
        label.htmlFor = id;
      });

      ['shape', 'print-spec', 'white-plate', 'white-offset', 'cut-path'].forEach((fieldName) => {
        part.querySelectorAll(`[data-field="${fieldName}"]`).forEach((input) => {
          input.name = `${fieldName}-${sectionId}-${partId}`;
        });
      });

      const body = part.querySelector('[data-part-body]');
      const toggle = part.querySelector('[data-toggle-part]');
      if (body && toggle) {
        body.id = `AcrylicPartBody-${sectionId}-${partId}`;
        toggle.setAttribute('aria-controls', body.id);
      }
    }

    addPart() {
      this.markUserInteraction();
      this.createPart();
    }

    createPart({
      initial = false,
      copyFrom = null,
      copyDimensions = false,
      preset = null,
      skipCalculate = false,
      focus = true
    } = {}) {
      if (!initial && this.getParts().length >= this.maximumParts) return null;
      if (!initial && this.lastEstimate) {
        this.getParts().forEach((existingPart, index) => {
          if (this.lastEstimate.parts[index]?.valid) this.setPartExpanded(existingPart, false);
        });
      }
      const fragment = this.partTemplate.content.cloneNode(true);
      const part = fragment.querySelector('[data-part]');
      const partId = this.nextPartId;
      this.nextPartId += 1;
      part.dataset.partId = String(partId);
      this.assignPartFieldIds(part, partId);
      this.partsList.appendChild(fragment);
      this.updatePartShapeFields(part);
      this.updatePartPrintOptions(part);
      if (copyFrom) this.copyPartSpecifications(copyFrom, part, { copyDimensions, copyProcesses: true });
      if (preset) this.applyDraftPart(part, preset);
      this.updatePartControls();
      if (!skipCalculate) this.calculate();
      this.setPartExpanded(part, true);
      if (!initial && focus) part.querySelector('[data-toggle-part]')?.focus();
      return part;
    }

    addProcess(part) {
      if (!this.processTemplate || this.getProcessRows(part).length >= this.maximumProcessesPerPart) return;
      const created = this.createProcessRow(part);
      if (!created) return;
      this.updateProcessControls(part);
      this.calculate();
      window.requestAnimationFrame(() => {
        created.row.scrollIntoView({ behavior: this.getScrollBehavior(), block: 'nearest' });
        created.categoryInput.focus({ preventScroll: true });
      });
    }

    createProcessRow(part, preset = {}) {
      if (!this.processTemplate || this.getProcessRows(part).length >= this.maximumProcessesPerPart) return null;
      const fragment = this.processTemplate.content.cloneNode(true);
      const row = fragment.querySelector('[data-process-row]');
      const processId = this.nextProcessId;
      this.nextProcessId += 1;
      const sectionId = this.root.id.replace('AcrylicQuote-', '');
      const partId = part.dataset.partId;
      const categoryInput = row.querySelector('[data-process-category]');
      const specInput = row.querySelector('[data-process-spec]');
      const categoryId = `AcrylicProcessCategory-${sectionId}-${partId}-${processId}`;
      const specId = `AcrylicProcessSpec-${sectionId}-${partId}-${processId}`;
      categoryInput.id = categoryId;
      specInput.id = specId;
      row.querySelector('[data-label-for="process-category"]').htmlFor = categoryId;
      row.querySelector('[data-label-for="process-spec"]').htmlFor = specId;
      part.querySelector('[data-process-list]').appendChild(fragment);
      this.populateProcessCategorySelect(row);
      if (preset.category) categoryInput.value = preset.category;
      this.populateProcessSpecSelect(row);
      if (preset.spec) {
        specInput.value = preset.spec;
        this.updateProcessRow(row);
      }
      return { row, categoryInput, specInput };
    }

    updatePartControls() {
      const parts = this.getParts();
      const isOnlyPart = parts.length === 1;
      parts.forEach((part, index) => {
        const title = this.formatPartTitle(index + 1);
        const titleElement = part.querySelector('[data-part-title]');
        if (titleElement) titleElement.textContent = title;
        const removeButton = part.querySelector('[data-remove-part]');
        removeButton.disabled = isOnlyPart;
        removeButton.setAttribute('aria-label', `${title}を削除`);
        const toggleButton = part.querySelector('[data-toggle-part]');
        if (toggleButton) {
          const expanded = toggleButton.getAttribute('aria-expanded') === 'true';
          toggleButton.setAttribute('aria-label', `${title}の入力欄を${expanded ? '閉じる' : '開く'}`);
        }
        const copyPrevious = part.querySelector('[data-copy-previous]');
        if (copyPrevious) copyPrevious.hidden = index === 0;
        const applyIntake = part.querySelector('[data-apply-intake]');
        if (applyIntake) applyIntake.hidden = parts.length <= 1;
        const duplicate = part.querySelector('[data-duplicate-part]');
        if (duplicate) duplicate.disabled = parts.length >= this.maximumParts;
        this.updatePartShapeFields(part);
        this.updatePartPrintOptions(part);
        this.updateProcessControls(part);
      });
      this.partsCount.textContent = `${parts.length}／${this.maximumParts}`;
      this.addButton.disabled = parts.length >= this.maximumParts;
      this.partsHelp.textContent = parts.length >= this.maximumParts
        ? this.config.texts.partsLimitReached || '追加できるパーツは10点までです。'
        : this.config.texts.partsLimit || '最大10パーツまで追加できます。';
    }

    updatePartShapeFields(part) {
      if (!part) return;
      const shapeInput = part.querySelector('[data-field="shape"]:checked');
      const isCircle = shapeInput && shapeInput.value === 'circle';
      part.querySelectorAll('[data-shape-field="standard"]').forEach((field) => {
        field.hidden = isCircle;
        field.querySelectorAll('input').forEach((input) => {
          input.disabled = isCircle;
        });
      });
      part.querySelectorAll('[data-shape-field="circle"]').forEach((field) => {
        field.hidden = !isCircle;
        field.querySelectorAll('input').forEach((input) => {
          input.disabled = !isCircle;
        });
      });
    }

    updatePartPrintOptions(part) {
      if (!part) return;
      const printInput = part.querySelector('[data-field="print-spec"]:checked');
      const printSpec = this.getOption('printSpecs', printInput ? printInput.value : '', '印刷未設定');
      const noPrint = Boolean(printSpec.key && printSpec.noPrint);
      const whiteSection = part.querySelector('[data-option-section="white-plate"]');
      const offsetOptions = part.querySelector('[data-white-offset-options]');
      const note = part.querySelector('[data-white-conditional-note]');
      const whitePlateInput = part.querySelector('[data-field="white-plate"]:checked');
      const showWhiteOptions = Boolean(printSpec.key && !noPrint);
      const showOffsetOptions = Boolean(showWhiteOptions && whitePlateInput);

      if (whiteSection) {
        whiteSection.hidden = !showWhiteOptions;
        whiteSection.querySelectorAll('[data-field="white-plate"]').forEach((input) => {
          input.disabled = !showWhiteOptions;
        });
      }
      if (offsetOptions) {
        offsetOptions.hidden = !showOffsetOptions;
        offsetOptions.querySelectorAll('[data-field="white-offset"]').forEach((input) => {
          input.disabled = !showOffsetOptions;
        });
      }
      if (note) {
        note.classList.toggle('is-no-print', noPrint);
        note.textContent = !printSpec.key
          ? '印刷仕様を選択すると、必要な白版設定が表示されます。'
          : (noPrint
            ? '印刷なし（透明仕上げ）のため、白版設定は不要です。'
            : '印刷仕様に合わせて白版を選択してください。');
      }
    }

    getRadioValue(part, fieldName) {
      return part.querySelector(`[data-field="${fieldName}"]:checked`)?.value || '';
    }

    setRadioValue(part, fieldName, value) {
      part.querySelectorAll(`[data-field="${fieldName}"]`).forEach((input) => {
        input.checked = Boolean(value && input.value === value);
      });
    }

    copyPartSpecifications(source, target, { copyDimensions = false, copyProcesses = false } = {}) {
      if (!source || !target) return;
      if (copyDimensions) {
        this.setRadioValue(target, 'shape', this.getRadioValue(source, 'shape'));
        ['width', 'height', 'diameter'].forEach((fieldName) => {
          const sourceInput = source.querySelector(`[data-field="${fieldName}"]`);
          const targetInput = target.querySelector(`[data-field="${fieldName}"]`);
          if (sourceInput && targetInput) targetInput.value = sourceInput.value;
        });
      }
      ['print-spec', 'white-plate', 'white-offset', 'cut-path'].forEach((fieldName) => {
        this.setRadioValue(target, fieldName, this.getRadioValue(source, fieldName));
      });
      if (copyProcesses) this.copyProcesses(source, target);
      this.updatePartShapeFields(target);
      this.updatePartPrintOptions(target);
    }

    copyProcesses(source, target) {
      const presets = this.getProcessRows(source).map((row) => ({
        category: row.querySelector('[data-process-category]')?.value || '',
        spec: row.querySelector('[data-process-spec]')?.value || ''
      }));
      const targetList = target.querySelector('[data-process-list]');
      if (!targetList) return;
      targetList.innerHTML = '';
      presets.forEach((preset) => this.createProcessRow(target, preset));
      this.updateProcessControls(target);
    }

    copyPreviousSpecifications(part) {
      const parts = this.getParts();
      const index = parts.indexOf(part);
      if (index <= 0) return;
      this.copyPartSpecifications(parts[index - 1], part, {
        copyDimensions: true,
        copyProcesses: true
      });
      const processDetails = part.querySelector('[data-process-details]');
      if (processDetails && this.getProcessRows(part).length > 0) processDetails.open = true;
      this.calculate();
      this.showPartActionStatus(part, '前のパーツの仕様をコピーしました。');
    }

    applyIntakeToAll(source) {
      const parts = this.getParts();
      if (parts.length <= 1) return;
      parts.forEach((target) => {
        if (target !== source) this.copyPartSpecifications(source, target);
      });
      this.calculate();
      this.showPartActionStatus(source, '印刷・白版・カットライン設定をすべてのパーツに適用しました。');
    }

    showPartActionStatus(part, message) {
      const status = part.querySelector('[data-part-action-status]');
      if (!status) return;
      status.textContent = message;
      window.setTimeout(() => {
        if (status.textContent === message) status.textContent = '';
      }, 3500);
    }

    updateProcessControls(part) {
      const rows = this.getProcessRows(part);
      const count = part.querySelector('[data-process-count]');
      const details = part.querySelector('[data-process-details]');
      const addButton = part.querySelector('[data-add-process]');
      const complete = rows.every((row) => Boolean(
        row.querySelector('[data-process-category]')?.value
        && row.querySelector('[data-process-spec]')?.value
      ));
      if (count) count.textContent = rows.length === 0
        ? '選択なし'
        : complete ? `${rows.length}件 ✓` : `${rows.length}件・要確認`;
      this.setStepState(details, complete);
      if (addButton) addButton.disabled = rows.length >= this.maximumProcessesPerPart;
      rows.forEach((row) => this.updateProcessRow(row));
    }

    updateProcessRow(row) {
      const specInput = row.querySelector('[data-process-spec]');
      const helpButton = row.querySelector('[data-process-help]');
      helpButton.disabled = !specInput.value;
    }

    openProcessDialog(button) {
      const row = button.closest('[data-process-row]');
      const specInput = row.querySelector('[data-process-spec]');
      const option = this.getOption('holeSpecs', specInput.value, '追加加工');
      this.openOptionDialog(option, '追加加工', button);
      if (this.whiteGuideLink) this.whiteGuideLink.hidden = true;
    }

    openWhiteGuide(button) {
      const option = this.getOption('whiteGuides', button.dataset.whiteGuide, '白版について');
      this.openOptionDialog(option, '白版について', button);
      if (this.whiteGuideLink) this.whiteGuideLink.hidden = false;
    }

    openOptionDialog(option, fallbackTitle, trigger) {
      this.lastDialogTrigger = trigger || document.activeElement;
      this.optionDialogTitle.textContent = option.title || option.label || fallbackTitle;
      this.optionDialogSize.textContent = option.size || option.badge || '';
      this.optionDialogSize.classList.toggle('is-badge', Boolean(option.badge && !option.size));
      this.optionDialogDescription.textContent = option.description || '';
      this.optionDialogNote.textContent = option.note || '';
      this.optionDialogFigure.innerHTML = '';
      let hasFigure = false;
      if (option.image) {
        const image = document.createElement('img');
        image.src = option.image;
        image.alt = option.imageAlt || option.title || option.label || fallbackTitle;
        image.loading = 'lazy';
        this.optionDialogFigure.appendChild(image);
        hasFigure = true;
      } else if (option.illustration) {
        this.optionDialogFigure.appendChild(this.createWhiteGuideIllustration(
          option.illustration,
          option.imageAlt || option.title || fallbackTitle
        ));
        hasFigure = true;
      }
      this.optionDialogFigure.hidden = !hasFigure;
      this.optionDialog.hidden = false;
      document.documentElement.classList.add('acrylic-quote-dialog-open');
      this.updateFloatingSummaryVisibility();
      this.optionDialogClose.focus();
    }

    createWhiteGuideIllustration(type, altText) {
      const wrapper = document.createElement('div');
      wrapper.className = 'acrylic-quote__white-guide-illustration';
      wrapper.setAttribute('role', 'img');
      wrapper.setAttribute('aria-label', altText);
      const commonDefs = `
        <defs>
          <pattern id="quote-checker-${type}" width="18" height="18" patternUnits="userSpaceOnUse">
            <rect width="18" height="18" fill="#ffffff"/>
            <rect width="9" height="9" fill="#e5e7eb"/>
            <rect x="9" y="9" width="9" height="9" fill="#e5e7eb"/>
          </pattern>
          <linearGradient id="quote-color-${type}" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stop-color="#ff6200"/>
            <stop offset="0.52" stop-color="#ff9a57"/>
            <stop offset="1" stop-color="#007bff"/>
          </linearGradient>
        </defs>`;
      const full = `
        <svg viewBox="0 0 720 400" aria-hidden="true" focusable="false">
          ${commonDefs}
          <rect width="720" height="400" rx="20" fill="#f9fafb"/>
          <g font-family="Noto Sans JP, sans-serif" text-anchor="middle">
            <g transform="translate(28 36)">
              <rect width="190" height="300" rx="16" fill="#fff" stroke="#e5e7eb"/>
              <text x="95" y="34" fill="#111827" font-size="18" font-weight="700">カラー印刷</text>
              <path d="M95 78 C145 78 160 112 145 150 C174 185 144 242 95 258 C46 242 16 185 45 150 C30 112 45 78 95 78Z" fill="url(#quote-color-${type})"/>
            </g>
            <text x="244" y="195" fill="#007bff" font-size="34">＋</text>
            <g transform="translate(265 36)">
              <rect width="190" height="300" rx="16" fill="url(#quote-checker-${type})" stroke="#e5e7eb"/>
              <text x="95" y="34" fill="#111827" font-size="18" font-weight="700">全面白版</text>
              <path d="M95 78 C145 78 160 112 145 150 C174 185 144 242 95 258 C46 242 16 185 45 150 C30 112 45 78 95 78Z" fill="#fff" stroke="#007bff" stroke-width="4"/>
            </g>
            <text x="481" y="195" fill="#007bff" font-size="34">→</text>
            <g transform="translate(502 36)">
              <rect width="190" height="300" rx="16" fill="#fff" stroke="#e5e7eb"/>
              <text x="95" y="34" fill="#111827" font-size="18" font-weight="700">仕上がり</text>
              <path d="M95 78 C145 78 160 112 145 150 C174 185 144 242 95 258 C46 242 16 185 45 150 C30 112 45 78 95 78Z" fill="url(#quote-color-${type})" stroke="#fff" stroke-width="8"/>
              <text x="95" y="285" fill="#16a34a" font-size="15" font-weight="700">発色がはっきり</text>
            </g>
          </g>
        </svg>`;
      const partial = `
        <svg viewBox="0 0 720 400" aria-hidden="true" focusable="false">
          ${commonDefs}
          <rect width="720" height="400" rx="20" fill="#f9fafb"/>
          <g font-family="Noto Sans JP, sans-serif" text-anchor="middle">
            <g transform="translate(28 36)">
              <rect width="190" height="300" rx="16" fill="#fff" stroke="#e5e7eb"/>
              <text x="95" y="34" fill="#111827" font-size="18" font-weight="700">カラー印刷</text>
              <circle cx="95" cy="168" r="78" fill="url(#quote-color-${type})"/>
              <circle cx="95" cy="145" r="34" fill="#6fcddd"/>
            </g>
            <text x="244" y="195" fill="#007bff" font-size="34">＋</text>
            <g transform="translate(265 36)">
              <rect width="190" height="300" rx="16" fill="url(#quote-checker-${type})" stroke="#e5e7eb"/>
              <text x="95" y="34" fill="#111827" font-size="18" font-weight="700">部分白版</text>
              <path d="M30 168 A65 65 0 0 0 160 168 L160 230 A78 78 0 0 1 30 230Z" fill="#fff" stroke="#007bff" stroke-width="4"/>
              <text x="95" y="276" fill="#4b5563" font-size="14">上部は白版なし</text>
            </g>
            <text x="481" y="195" fill="#007bff" font-size="34">→</text>
            <g transform="translate(502 36)">
              <rect width="190" height="300" rx="16" fill="url(#quote-checker-${type})" stroke="#e5e7eb"/>
              <text x="95" y="34" fill="#111827" font-size="18" font-weight="700">仕上がり</text>
              <circle cx="95" cy="168" r="78" fill="url(#quote-color-${type})" fill-opacity="0.45" stroke="#007bff" stroke-width="3"/>
              <path d="M30 168 A65 65 0 0 0 160 168 L160 230 A78 78 0 0 1 30 230Z" fill="url(#quote-color-${type})"/>
              <text x="95" y="276" fill="#007bff" font-size="14" font-weight="700">透明感を残す</text>
            </g>
          </g>
        </svg>`;
      const offset = `
        <svg viewBox="0 0 720 400" aria-hidden="true" focusable="false">
          ${commonDefs}
          <rect width="720" height="400" rx="20" fill="#f9fafb"/>
          <g font-family="Noto Sans JP, sans-serif" text-anchor="middle">
            <g transform="translate(35 36)">
              <rect width="300" height="300" rx="16" fill="#fff" stroke="#e5e7eb"/>
              <text x="150" y="36" fill="#111827" font-size="18" font-weight="700">縮小しない</text>
              <circle cx="150" cy="164" r="92" fill="#fff" stroke="#ef4444" stroke-width="12"/>
              <circle cx="158" cy="156" r="82" fill="url(#quote-color-${type})"/>
              <path d="M62 90 L92 116" stroke="#ef4444" stroke-width="3"/>
              <text x="76" y="82" fill="#ef4444" font-size="15" font-weight="700">白フチ</text>
              <text x="150" y="282" fill="#ef4444" font-size="15" font-weight="700">位置ずれが目立ちやすい</text>
            </g>
            <text x="360" y="195" fill="#007bff" font-size="34">→</text>
            <g transform="translate(385 36)">
              <rect width="300" height="300" rx="16" fill="#fff" stroke="#e5e7eb"/>
              <text x="150" y="36" fill="#111827" font-size="18" font-weight="700">自動で縮小</text>
              <circle cx="150" cy="164" r="88" fill="url(#quote-color-${type})"/>
              <circle cx="150" cy="164" r="78" fill="none" stroke="#fff" stroke-width="3" stroke-dasharray="8 7"/>
              <circle cx="242" cy="82" r="22" fill="#16a34a"/>
              <path d="M232 82 L240 90 L253 74" fill="none" stroke="#fff" stroke-width="5" stroke-linecap="round" stroke-linejoin="round"/>
              <text x="150" y="282" fill="#16a34a" font-size="15" font-weight="700">白フチを目立ちにくく</text>
            </g>
          </g>
        </svg>`;
      wrapper.innerHTML = type === 'partial' ? partial : (type === 'offset' ? offset : full);
      return wrapper;
    }

    closeOptionDialog() {
      if (this.optionDialog.hidden) return;
      this.optionDialog.hidden = true;
      document.documentElement.classList.remove('acrylic-quote-dialog-open');
      this.updateFloatingSummaryVisibility();
      if (this.lastDialogTrigger && document.contains(this.lastDialogTrigger)) {
        this.lastDialogTrigger.focus({ preventScroll: true });
      }
    }

    handleDialogKeydown(event) {
      if (this.optionDialog.hidden) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeOptionDialog();
      }
      if (event.key === 'Tab') {
        event.preventDefault();
        this.optionDialogClose.focus();
      }
    }

    getQuoteDates() {
      const createdAt = new Date();
      const validUntil = new Date(
        createdAt.getTime() + (this.config.quoteValidityDays * 24 * 60 * 60 * 1000)
      );
      const formatter = new Intl.DateTimeFormat('ja-JP', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        timeZone: 'Asia/Tokyo'
      });
      return {
        createdAt: formatter.format(createdAt),
        validUntil: formatter.format(validUntil)
      };
    }

    getWhitePlateLabel(value) {
      if (value === 'none') return '白版なし';
      return value === 'full' ? '全面に白版' : '一部に白版';
    }

    getWhiteOffsetLabel(value) {
      return value === 'auto' ? '自動で縮小する' : '縮小しない';
    }

    getCutPathLabel(value) {
      return value === 'custom'
        ? 'カットパスデータを入稿'
        : '当店で作成（画像の輪郭をもとに調整）';
    }

    getPartSizeLabel(part) {
      return part.shape === 'circle'
        ? `円形・直径${part.diameter}mm`
        : `横×縦計算・横${part.width}mm × 縦${part.height}mm`;
    }

    getCompactPartLine(part) {
      const printSpec = this.getOption('printSpecs', part.printSpecKey, '印刷未設定');
      const size = part.shape === 'circle' ? `直径${part.diameter}mm` : `${part.width}×${part.height}mm`;
      const details = [size, printSpec.shortLabel || printSpec.label];

      if (!part.noPrint) {
        details.push(part.whitePlate === 'full' ? '全面白版' : '一部白版');
        if (part.whiteOffset !== 'auto') details.push('白版縮小なし');
      }
      if (part.cutPath === 'custom') details.push('カットパス入稿');
      if (part.processes.length > 0) {
        details.push(`加工：${part.processes.map((item) => item.shortLabel).join('+')}`);
      }

      return `${this.formatPartTitle(part.index).replace('アクリルパーツ', '')} ${details.join('／')}`;
    }

    buildCopyText() {
      const estimate = this.lastEstimate;
      const dates = this.getQuoteDates();
      const partLines = estimate.parts.map((part) => this.getCompactPartLine(part));
      const commonLines = [
        '【カスタムアクリル概算見積もり】',
        `商品：${estimate.product.label}`,
        `数量：${estimate.quantity}個（全パーツ共通）`,
        ...(estimate.processingMethod === 'cnc' ? ['カット：CNC切削'] : []),
        'パーツ仕様：',
        ...partLines
      ];
      const resultLines = estimate.manualInquiry
        ? [
          '判定：個別見積もり',
          ...estimate.manualReasons.map((reason) => `理由：${reason}`),
        ]
        : [
          `1セットあたり：${this.formatMoney(estimate.finalUnitPrice)}（税込・目安）`,
          `概算合計：${this.formatMoney(estimate.total)}（税込・送料別）`,
          `有効期限：${dates.validUntil}`
        ];

      return [
        ...commonLines,
        ...resultLines
      ].join('\n');
    }

    buildOrderMessage() {
      const estimate = this.lastEstimate;
      const dates = this.getQuoteDates();
      const maximumLength = 500;
      const safeProductLabel = Array.from(estimate.product.label || 'カスタムアクリル')
        .slice(0, 60)
        .join('');
      const cutting = estimate.processingMethod === 'cnc' ? '／CNC切削' : '';
      const headerLines = [
        `【概算見積】${safeProductLabel}`,
        `数量：${estimate.quantity}個${cutting}`
      ];
      const footerLines = [
        `単価：${this.formatMoney(estimate.finalUnitPrice)}／合計：${this.formatMoney(estimate.total)}（税込・送料別）`,
        `期限：${dates.validUntil}`
      ];
      const minimumPartLines = estimate.parts.map((part) => {
        const marker = this.formatPartTitle(part.index).replace('アクリルパーツ', '');
        const size = part.shape === 'circle' ? `直径${part.diameter}mm` : `${part.width}×${part.height}mm`;
        return `${marker} ${size}`;
      });
      const fullPartLines = estimate.parts.map((part, index) => {
        const printSpec = this.getOption('printSpecs', part.printSpecKey, '印刷未設定');
        const extras = [printSpec.shortLabel || printSpec.label];
        if (!part.noPrint) {
          extras.push(part.whitePlate === 'full' ? '全面白版' : '部分白版');
          if (part.whiteOffset !== 'auto') extras.push('白版縮小なし');
        }
        if (part.cutPath === 'custom') extras.push('カットパス入稿');
        if (part.processes.length > 0) {
          extras.push(`加工:${part.processes.map((item) => item.shortLabel).join('+')}`);
        }
        return `${minimumPartLines[index]}／${extras.join('／')}`;
      });
      const fullMessage = [...headerLines, ...fullPartLines, ...footerLines].join('\n');
      if (Array.from(fullMessage).length <= maximumLength) return fullMessage;

      const minimumMessage = [...headerLines, ...minimumPartLines, ...footerLines].join('\n');
      let remainingExtras = Math.max(0, maximumLength - Array.from(minimumMessage).length);
      let remainingParts = minimumPartLines.length;
      const fittedPartLines = fullPartLines.map((line, index) => {
        const minimumLine = minimumPartLines[index];
        const desiredExtra = Array.from(line).slice(Array.from(minimumLine).length);
        const allowance = Math.floor(remainingExtras / Math.max(1, remainingParts));
        remainingParts -= 1;
        if (desiredExtra.length <= allowance) {
          remainingExtras -= desiredExtra.length;
          return line;
        }
        if (allowance < 2) return minimumLine;
        remainingExtras -= allowance;
        return `${minimumLine}${desiredExtra.slice(0, allowance - 1).join('')}…`;
      });
      return [...headerLines, ...fittedPartLines, ...footerLines].join('\n');
    }

    prepareOrderHandoff(event) {
      if (!this.lastEstimate || !this.lastEstimate.orderable) {
        event.preventDefault();
        this.requestFullValidation();
        this.calculate();
        if (this.lastEstimate && !this.lastEstimate.manualInquiry) this.focusFirstIncomplete();
        this.showCopyStatus(this.lastEstimate && this.lastEstimate.manualInquiry
          ? '個別見積もりについてお問い合わせください。'
          : '入力内容を確認してから商品ページへ進んでください。');
        return;
      }
      const message = this.buildOrderMessage();
      const payload = {
        message,
        createdAt: Date.now()
      };
      try {
        window.sessionStorage.setItem('idworld:acrylic-quote-handoff', JSON.stringify(payload));
      } catch (error) {
        this.showCopyStatus(this.config.texts.handoffNotice || '見積もり内容を商品ページへ引き継ぎます。');
      }
      try {
        const activeOrderLink = event.currentTarget || this.orderLink;
        const targetUrl = new URL(activeOrderLink.href, window.location.href);
        targetUrl.searchParams.set('from_quote', '1');
        targetUrl.searchParams.set('aq_message', message);
        this.orderLinks.forEach((link) => {
          link.href = targetUrl.toString();
        });
      } catch (error) {
        this.orderLinks.forEach((link) => {
          link.href = this.lastEstimate.product.orderUrl;
        });
      }
    }

    async copyQuote() {
      if (!this.lastEstimate || !this.lastEstimate.inputValid) {
        this.requestFullValidation();
        this.calculate();
        this.focusFirstIncomplete();
        this.showCopyStatus('入力内容を確認してから見積もりをコピーしてください。');
        return;
      }
      const text = this.buildCopyText();
      try {
        let copied = false;

        if (navigator.clipboard && window.isSecureContext) {
          try {
            await navigator.clipboard.writeText(text);
            copied = true;
          } catch (clipboardError) {
            // Shopify's theme editor can deny Clipboard API access inside its preview iframe.
            // Continue to the selection-based fallback below.
          }
        }

        if (!copied) {
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.setAttribute('readonly', '');
          textarea.style.position = 'fixed';
          textarea.style.top = '0';
          textarea.style.left = '0';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.focus({ preventScroll: true });
          textarea.select();
          textarea.setSelectionRange(0, textarea.value.length);

          try {
            copied = document.execCommand('copy');
          } finally {
            textarea.remove();
          }
        }

        if (!copied) throw new Error('Copy failed');
        this.showCopyStatus(this.config.texts.copySuccess || '見積もり内容をコピーしました。');
      } catch (error) {
        this.showCopyStatus(this.config.texts.copyError || 'コピーできませんでした。内容を選択してコピーしてください。');
      }
    }

    showCopyStatus(message) {
      window.clearTimeout(this.copyStatusTimer);
      this.copyStatus.textContent = message;
      this.copyStatusTimer = window.setTimeout(() => {
        this.copyStatus.textContent = '';
      }, 4000);
    }
  }

  const initializeAcrylicQuotes = (scope = document) => {
    const roots = [];
    if (scope.matches && scope.matches('[data-acrylic-quote]')) roots.push(scope);
    roots.push(...scope.querySelectorAll('[data-acrylic-quote]'));
    roots.forEach((root) => {
      if (root.dataset.quoteInitialized === 'true') return;
      root.dataset.quoteInitialized = 'true';
      new AcrylicQuoteCalculator(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAcrylicQuotes());
  } else {
    initializeAcrylicQuotes();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeAcrylicQuotes(event.target);
  });
})();
